import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Users, ChevronRight, Search, UserPlus, Crown, Trash2, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";

const PROGRAMS = [
  { value: "men", label: "Men & Young Men" },
  { value: "women", label: "Women & Young Girls" },
];

export default function FacilitatorDashboard() {
  const { user } = useAuth();
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recent"); // recent | progress-desc | progress-asc | name | score
  const [showAdd, setShowAdd] = useState(false);
  const [busy, setBusy] = useState(false);

  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const { data } = await api.get("/facilitator/participants");
      setParticipants(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = useMemo(() => ({
    total: participants.length,
    avg: participants.length
      ? Math.round(participants.reduce((s, p) => s + (p.avg_score || 0), 0) / participants.length)
      : 0,
    fellows: participants.filter((p) => p.is_fellow).length,
  }), [participants]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let rows = participants;
    if (needle) {
      rows = rows.filter((p) =>
        (p.name || "").toLowerCase().includes(needle) ||
        (p.email || "").toLowerCase().includes(needle) ||
        (p.organization || "").toLowerCase().includes(needle));
    }
    const cmp = {
      "progress-desc": (a, b) => (b.completed_tracks || 0) - (a.completed_tracks || 0),
      "progress-asc": (a, b) => (a.completed_tracks || 0) - (b.completed_tracks || 0),
      "name": (a, b) => (a.name || "").localeCompare(b.name || ""),
      "score": (a, b) => (b.avg_score || 0) - (a.avg_score || 0),
      "recent": (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
    }[sort];
    return cmp ? [...rows].sort(cmp) : rows;
  }, [participants, q, sort]);

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete ${p.name} and all of their journals, assessments, and progress?\n\nThis cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/users/${p.id}`);
      toast.success(`Removed ${p.name}.`);
      setParticipants((rows) => rows.filter((r) => r.id !== p.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete this account.");
    } finally {
      setBusy(false);
    }
  };

  const handlePurgeDemos = async () => {
    if (!window.confirm("Permanently delete ALL demo accounts (facilitator@crownpath.org, participant@crownpath.org, etc.) and their data?\n\nProceed?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/purge-demos");
      toast.success(data.message);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not purge demo accounts.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-tcpi-bg min-h-screen" data-testid="facilitator-page">
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="overline mb-3">Facilitator</div>
        <h1
          className="text-4xl md:text-5xl text-[#F7F5F0] mb-2"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          Participants
        </h1>
        <p className="text-[#8A8782] mb-10">
          Walk with them. Hold the line. Lead by example.
        </p>

        {/* Stat tiles */}
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          <StatTile testid="stat-total" overline="Total Participants" value={stats.total} icon={Users} color="#F7F5F0" />
          <StatTile testid="stat-avg" overline="Average Score" value={`${stats.avg}%`} color="#C9A961" />
          <StatTile testid="stat-fellows" overline="Crown Fellows" value={stats.fellows} icon={Crown} color="#F7F5F0" />
        </div>

        {/* Toolbar */}
        <div className="card-bento mb-4 flex items-center gap-3 flex-wrap" data-testid="participants-toolbar">
          <div className="flex-1 min-w-[220px] relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8782]" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, email, or organization"
              className="input-tcpi pl-9"
              data-testid="participants-search"
            />
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="input-tcpi max-w-[220px]"
            data-testid="participants-sort"
          >
            <option value="recent">Most recently enrolled</option>
            <option value="progress-desc">Furthest along</option>
            <option value="progress-asc">Needs attention (least progress)</option>
            <option value="score">Highest avg score</option>
            <option value="name">Name (A–Z)</option>
          </select>
          <button
            onClick={() => setShowAdd(true)}
            className="btn-primary inline-flex items-center gap-2 text-sm"
            data-testid="add-participant-btn"
          >
            <UserPlus className="w-4 h-4" /> Add Participant
          </button>
          {isAdmin && (
            <button
              onClick={handlePurgeDemos}
              disabled={busy}
              className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-red-700/40 text-red-300 hover:bg-red-900/15 transition disabled:opacity-50"
              data-testid="purge-demos-btn"
              title="Permanently delete demo accounts and their data"
            >
              <AlertTriangle className="w-3.5 h-3.5" /> Purge demo accounts
            </button>
          )}
        </div>

        {/* List */}
        <div className="card-bento p-0 overflow-hidden">
          {loading && <div className="p-8 text-[#8A8782]">Loading…</div>}
          {!loading && visible.length === 0 && (
            <div className="p-8 text-[#8A8782]" data-testid="no-participants">
              {q ? "No participants match your search." : "No participants yet — add your first one above."}
            </div>
          )}
          {visible.map((p, i) => (
            <ParticipantRow
              key={p.id}
              p={p}
              isFirst={i === 0}
              canDelete={isAdmin}
              onDelete={() => handleDelete(p)}
            />
          ))}
        </div>
      </section>

      {showAdd && (
        <AddParticipantModal
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            setShowAdd(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function StatTile({ overline, value, icon: Icon, color, testid }) {
  return (
    <div className="card-bento" data-testid={testid}>
      {Icon && <Icon className="w-6 h-6 text-[#C9A961] mb-3" strokeWidth={1.4} />}
      <div className="overline mb-1">{overline}</div>
      <div className="text-4xl" style={{ fontFamily: "Cormorant Garamond, serif", color }}>{value}</div>
    </div>
  );
}

function ParticipantRow({ p, isFirst, canDelete, onDelete }) {
  const borderTop = isFirst ? "" : "border-t border-[#C9A961]/10";
  return (
    <div className={`flex items-center justify-between px-6 py-5 hover:bg-[#1A1816] transition ${borderTop}`}>
      <Link to={`/facilitator/${p.id}`} className="flex-1 min-w-0" data-testid={`participant-row-${p.id}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[#F7F5F0]">{p.name}</div>
          {p.is_fellow && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#C9A961]/15 text-[#C9A961] text-[10px] tracking-wider"
              data-testid="fellow-badge"
            >
              <Crown className="w-3 h-3" /> CROWN FELLOW
            </span>
          )}
        </div>
        <div className="text-xs text-[#8A8782]">{p.email} · {p.organization || "—"}</div>
      </Link>
      <div className="flex items-center gap-8 text-right">
        <div>
          <div className="overline text-[10px]">Tracks</div>
          <div className="text-[#C9A961]">{p.completed_tracks}/{p.total_tracks || 7}</div>
        </div>
        <div>
          <div className="overline text-[10px]">Avg Score</div>
          <div className="text-[#C9A961]">{p.avg_score}%</div>
        </div>
        {canDelete && (
          <button
            onClick={onDelete}
            className="text-[#8A8782] hover:text-red-400 transition p-2"
            title={`Delete ${p.name}`}
            data-testid={`delete-participant-${p.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
        <Link to={`/facilitator/${p.id}`} className="text-[#8A8782]">
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function AddParticipantModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", program: "men", role: "participant" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      // Single-use temp password. The participant resets it on first login.
      // Built from `crypto.getRandomValues` (CSPRNG) so we don't reveal a
      // predictable literal pattern in the bundle.
      const tempPassword = generateTempPassword();
      await api.post("/admin/users", {
        ...form,
        password: tempPassword,
        organization: "",
      });
      toast.success(`Added ${form.name}. Temp password: ${tempPassword}`);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add participant.");
    } finally {
      setSubmitting(false);
    }
  };

  function generateTempPassword() {
    const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    const bytes = new Uint8Array(14);
    window.crypto.getRandomValues(bytes);
    let pw = "";
    for (const b of bytes) pw += alphabet[b % alphabet.length];
    return pw + "!";
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0A0908]/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="add-participant-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-[#1A1816] border border-[#C9A961]/30 rounded-lg p-6 w-full max-w-md shadow-2xl"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-2xl text-[#F7F5F0]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            Add Participant
          </h3>
          <button type="button" onClick={onClose} className="text-[#8A8782] hover:text-[#C9A961]" data-testid="add-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4">
          <Field label="Legal name">
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Marcus Allen"
              className="input-tcpi"
              data-testid="add-participant-name"
              autoFocus
            />
          </Field>
          <Field label="Email">
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })}
              placeholder="marcus@example.com"
              className="input-tcpi"
              data-testid="add-participant-email"
            />
          </Field>
          <Field label="Program">
            <select
              value={form.program}
              onChange={(e) => setForm({ ...form, program: e.target.value })}
              className="input-tcpi"
              data-testid="add-participant-program"
            >
              {PROGRAMS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </Field>
        </div>
        <div className="flex items-center justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="add-participant-submit"
          >
            <UserPlus className="w-4 h-4" />
            {submitting ? "Adding…" : "Add Participant"}
          </button>
        </div>
        <p className="text-[10px] text-[#8A8782] italic mt-4 leading-relaxed">
          A temporary password is generated and shown once. Share it with the participant — they can change it from Account Settings on first login.
        </p>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs text-[#8A8782] mb-1.5 uppercase tracking-wider">{label}</span>
      {children}
    </label>
  );
}
